import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocLink,
  DocNavFooter,
  DocCard,
} from "@/components/docs";
import { ApiEndpoint, CodeBlock, Parameter } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import {
  EnvelopeIcon,
  CommandLineIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

export default function ApiContactsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Contacts API" },
  ];

  const listContactsQueryParams: Parameter[] = [
    {
      name: "page",
      type: "integer",
      required: false,
      description: "Page number for pagination",
      default: "1",
      example: "2",
    },
    {
      name: "per_page",
      type: "integer",
      required: false,
      description: "Items per page (max 100)",
      default: "50",
      example: "25",
    },
    {
      name: "search",
      type: "string",
      required: false,
      description: "Search contacts by name",
      example: "production",
    },
    {
      name: "channel",
      type: "string",
      required: false,
      description: "Filter by channel type",
      example: "email",
    },
    {
      name: "sort",
      type: "string",
      required: false,
      description: "Sort field",
      default: "created_at",
      example: "name",
    },
    {
      name: "order",
      type: "string",
      required: false,
      description: "Sort order",
      default: "desc",
      example: "asc",
    },
  ];

  const createContactBodyParams: Parameter[] = [
    {
      name: "name",
      type: "string",
      required: true,
      description: "Descriptive name for the contact",
      example: "DevOps Team Email",
    },
    {
      name: "channel",
      type: "string",
      required: true,
      description: "Type of notification channel",
      example: "email",
    },
    {
      name: "details",
      type: "object",
      required: true,
      description: "Channel-specific configuration (varies by channel type)",
    },
    {
      name: "active",
      type: "boolean",
      required: false,
      description: "Whether the contact is active",
      default: "true",
    },
    {
      name: "down_alerts_only",
      type: "boolean",
      required: false,
      description: "Only send alerts when monitors go down (not recovery)",
      default: "false",
    },
  ];

  const updateContactBodyParams: Parameter[] = [
    {
      name: "name",
      type: "string",
      required: false,
      description: "New descriptive name for the contact",
      example: "Updated Team Email",
    },
    {
      name: "details",
      type: "object",
      required: false,
      description: "Updated channel-specific configuration",
    },
    {
      name: "active",
      type: "boolean",
      required: false,
      description: "Whether the contact is active",
    },
    {
      name: "down_alerts_only",
      type: "boolean",
      required: false,
      description: "Only send alerts when monitors go down",
    },
  ];

  const pathParams: Parameter[] = [
    {
      name: "id",
      type: "string",
      required: true,
      description: "Contact ID (24-character MongoDB ObjectID)",
      example: "507f1f77bcf86cd799439011",
    },
  ];

  return (
    <DocLayout
      title="Contacts API - Uptime Monitor Documentation"
      description="Manage notification contacts and channels with the Contacts API"
      canonical="https://uptime-monitor.io/docs/api/contacts"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Contacts API"
        description="Manage notification contacts including email, webhooks, Slack, Discord, PagerDuty, and more. Configure who gets alerted when monitors detect issues."
      />

      <DocAlert variant="info" className="mb-8">
        <p>
          Contacts define how and where to send notifications when your monitors detect issues. Each
          contact has a specific channel type with its own configuration requirements.
        </p>
      </DocAlert>

      {/* Quick Reference */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>
          Supported Channels
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <DocCard className="p-4 text-center">
            <EnvelopeIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Email</div>
            <div className={`text-sm ${docsColors.text.muted}`}>SMTP delivery</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <CommandLineIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Webhook</div>
            <div className={`text-sm ${docsColors.text.muted}`}>HTTP callbacks</div>
          </DocCard>

          <DocCard className="p-4 text-center">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <div className={`font-semibold ${docsColors.text.heading}`}>Chat</div>
            <div className={`text-sm ${docsColors.text.muted}`}>Slack, Discord</div>
          </DocCard>
        </div>

        <p className={`${docsColors.text.body}`}>
          Plus integrations with <strong>PagerDuty</strong>, <strong>incident.io</strong>,{" "}
          <strong>Opsgenie</strong>, and <strong>Zendesk</strong> for advanced incident management
          workflows.
        </p>
      </section>

      {/* List Contacts */}
      <ApiEndpoint
        method="GET"
        path="/api/contacts"
        description="Retrieve a paginated list of all contacts in your account. Supports filtering and searching."
        queryParameters={listContactsQueryParams}
        requestExample={`curl -X GET "https://api.uptime-monitor.io/api/contacts?page=1&per_page=25" \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        responseExample={`{
  "status": "ok",
  "contacts": [
    {
      "id": "507f1f77bcf86cd799439011",
      "name": "DevOps Team Email",
      "channel": "email",
      "created_at": 1704067200,
      "details": {
        "email": "devops@example.com"
      },
      "active": true,
      "down_alerts_only": false,
      "error": null
    },
    {
      "id": "507f1f77bcf86cd799439012",
      "name": "On-call Team",
      "channel": "pagerduty",
      "created_at": 1704153600,
      "details": {
        "integration_key": "[REDACTED]",
        "severity_mapping": {
          "critical": "critical",
          "high": "error",
          "medium": "warning",
          "low": "info"
        },
        "auto_resolve_incidents": true
      },
      "active": true,
      "down_alerts_only": false,
      "error": null
    }
  ],
  "pagination": {
    "page": 1,
    "per_page": 25,
    "total": 2,
    "total_pages": 1,
    "has_next": false,
    "has_prev": false
  }
}`}
      />

      {/* Get Contact */}
      <ApiEndpoint
        method="GET"
        path="/api/contacts/:id"
        description="Retrieve details for a specific contact by ID."
        parameters={pathParams}
        requestExample={`curl -X GET https://api.uptime-monitor.io/api/contacts/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        responseExample={`{
  "status": "ok",
  "contact": {
    "id": "507f1f77bcf86cd799439011",
    "name": "DevOps Team Email",
    "channel": "email",
    "created_at": 1704067200,
    "details": {
      "email": "devops@example.com"
    },
    "active": true,
    "down_alerts_only": false,
    "error": null
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "CONTACT_NOT_FOUND",
            description: "Contact with the specified ID does not exist",
          },
        ]}
      />

      {/* Create Contact */}
      <ApiEndpoint
        method="POST"
        path="/api/contacts"
        description="Create a new notification contact. The required fields in 'details' vary by channel type."
        bodyParameters={createContactBodyParams}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/contacts \\
  -H "Authorization: Bearer um_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "DevOps Team Email",
    "channel": "email",
    "details": {
      "email": "devops@example.com"
    },
    "active": true,
    "down_alerts_only": false
  }'`}
        requestExampleLanguage="bash"
        responseExample={`{
  "status": "ok",
  "contact": {
    "id": "507f1f77bcf86cd799439011",
    "name": "DevOps Team Email",
    "channel": "email",
    "created_at": 1704067200,
    "details": {
      "email": "devops@example.com"
    },
    "active": true,
    "down_alerts_only": false,
    "error": null
  }
}`}
        errorResponses={[
          {
            status: 422,
            code: "VALIDATION_ERROR",
            description: "Required fields are missing or invalid",
          },
        ]}
      />

      {/* Update Contact */}
      <ApiEndpoint
        method="PUT"
        path="/api/contacts/:id"
        description="Update an existing contact. All fields are optional - only provided fields will be updated."
        parameters={pathParams}
        bodyParameters={updateContactBodyParams}
        requestExample={`curl -X PUT https://api.uptime-monitor.io/api/contacts/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Updated DevOps Email",
    "details": {
      "email": "new-devops@example.com"
    },
    "active": false
  }'`}
        requestExampleLanguage="bash"
        responseExample={`{
  "status": "ok",
  "contact": {
    "id": "507f1f77bcf86cd799439011",
    "name": "Updated DevOps Email",
    "channel": "email",
    "created_at": 1704067200,
    "details": {
      "email": "new-devops@example.com"
    },
    "active": false,
    "down_alerts_only": false,
    "error": null
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "CONTACT_NOT_FOUND",
            description: "Contact with the specified ID does not exist",
          },
          {
            status: 403,
            code: "READONLY_CHANNEL",
            description: "This contact channel cannot be modified (e.g., APNS)",
          },
        ]}
      />

      {/* Delete Contact */}
      <ApiEndpoint
        method="DELETE"
        path="/api/contacts/:id"
        description="Permanently delete a contact. This action cannot be undone."
        parameters={pathParams}
        requestExample={`curl -X DELETE https://api.uptime-monitor.io/api/contacts/507f1f77bcf86cd799439011 \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        responseExample="HTTP 204 No Content"
        errorResponses={[
          {
            status: 404,
            code: "CONTACT_NOT_FOUND",
            description: "Contact with the specified ID does not exist",
          },
          {
            status: 403,
            code: "READONLY_CHANNEL",
            description: "This contact channel cannot be deleted (e.g., APNS)",
          },
        ]}
      />

      {/* Reactivate Contact */}
      <ApiEndpoint
        method="POST"
        path="/api/contacts/:id/reactivate"
        description="Reactivate a contact that was automatically deactivated due to delivery failures. Contacts may be deactivated when repeated delivery attempts fail (e.g., invalid webhook URL, expired API key)."
        parameters={pathParams}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/contacts/507f1f77bcf86cd799439011/reactivate \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "contact": {
      "id": "507f1f77bcf86cd799439011",
      "name": "Production Slack",
      "channel": "slack",
      "active": true,
      "down_alerts_only": false
    }
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "CONTACT_NOT_FOUND",
            description: "Contact with the specified ID does not exist",
          },
        ]}
      />

      {/* Test Contact */}
      <ApiEndpoint
        method="POST"
        path="/api/contacts/:id/test"
        description="Send a test notification to verify the contact is configured correctly. Useful for validating webhook URLs, API keys, and integration settings."
        parameters={pathParams}
        requestExample={`curl -X POST https://api.uptime-monitor.io/api/contacts/507f1f77bcf86cd799439011/test \\
  -H "Authorization: Bearer um_your_api_key_here"`}
        responseExample={`{
  "status": "ok",
  "data": {
    "message": "Test notification sent successfully"
  }
}`}
        errorResponses={[
          {
            status: 404,
            code: "CONTACT_NOT_FOUND",
            description: "Contact with the specified ID does not exist",
          },
        ]}
      />

      {/* Channel Types */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Channel Types & Configuration
        </h2>

        <p className={`${docsColors.text.body} mb-6`}>
          Each channel type requires specific fields in the <code>details</code> object. Here are
          the supported channels and their configuration:
        </p>

        <div className="space-y-8">
          {/* Email */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Email</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Send notifications via email. Most basic and reliable notification method.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "email",
  "details": {
    "email": "alerts@example.com"
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Webhook */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Webhook</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              HTTP POST requests to your custom endpoint. Perfect for custom integrations.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "webhook",
  "details": {
    "url": "https://example.com/webhook"
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Slack */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Slack</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Send alerts to Slack channels using incoming webhooks.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "slack",
  "details": {
    "webhook_url": "https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX"
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Discord */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Discord</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Send alerts to Discord channels using webhooks.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "discord",
  "details": {
    "webhook_url": "https://discord.com/api/webhooks/1234567890/abcdefghijk"
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Microsoft Teams */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
              Microsoft Teams
            </h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Send alerts to Microsoft Teams channels using Workflow webhooks with Adaptive Card
              format.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "teams",
  "details": {
    "webhook_url": "https://your-org.webhook.office.com/webhookb2/..."
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* PagerDuty */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>PagerDuty</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Create incidents in PagerDuty with severity mapping and auto-resolution.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "pagerduty",
  "details": {
    "integration_key": "R01234567890123456789012345678901",
    "severity_mapping": {
      "critical": "critical",
      "high": "error",
      "medium": "warning",
      "low": "info"
    },
    "auto_resolve_incidents": true
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* incident.io */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>incident.io</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Create and manage incidents in incident.io with webhook integration.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "incidentio",
  "details": {
    "webhook_url": "https://api.incident.io/webhooks/...",
    "bearer_token": "inc_...",
    "auto_resolve_incidents": true
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Opsgenie */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Opsgenie</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Create alerts in Opsgenie with custom priority, responders, and tags.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "opsgenie",
  "details": {
    "api_key": "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    "priority": "P3",
    "responders": [
      {
        "type": "team",
        "id": "team-id"
      }
    ],
    "tags": ["uptime", "monitoring"],
    "auto_close_alerts": true,
    "eu_instance": false
  }
}`}
            </CodeBlock>
          </DocCard>

          {/* Zendesk */}
          <DocCard className="p-6">
            <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>Zendesk</h3>
            <p className={`${docsColors.text.body} mb-4`}>
              Create support tickets in Zendesk with custom fields and tags.
            </p>
            <CodeBlock language="json" title="Required Details">
              {`{
  "channel": "zendesk",
  "details": {
    "subdomain": "company",
    "email": "support@example.com",
    "api_token": "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    "priority": "normal",
    "custom_fields": [
      {
        "id": 360000000001,
        "value": "monitoring"
      }
    ],
    "tags": ["uptime", "alert"],
    "auto_solve_tickets": true
  }
}`}
            </CodeBlock>
          </DocCard>
        </div>
      </section>

      {/* Security Notes */}
      <DocAlert variant="warning" className="mb-8">
        <h3 className="font-semibold mb-2">Security & Privacy</h3>
        <ul className="space-y-1 text-sm">
          <li>
            • Sensitive fields (API keys, tokens) are redacted as <code>[REDACTED]</code> in API
            responses
          </li>
          <li>
            • APNS (Apple Push Notification) contacts are read-only and cannot be modified via API
          </li>
          <li>• All contacts are account-scoped - you can only access your own contacts</li>
          <li>• Store integration keys and tokens securely in your application</li>
        </ul>
      </DocAlert>

      <DocNavFooter
        prev={{ href: "/docs/api/authentication", text: "Authentication" }}
        next={{ href: "/docs/api/monitors", text: "Monitors API" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
